/**
 * Per-row selection checkbox for flow-level batch actions (focus view).
 * Renders nothing when outside a FlowSelectionProvider (e.g. list view).
 */
import { useTranslation } from "react-i18next";
import type { EditableCellProps } from "./types";
import { cn } from "@/lib/utils";
import { useFlowSelectionOptional } from "../../contexts/FlowSelectionContext";
import { SelectionCheckboxBox } from "../ui/SelectionCheckbox";

export function SelectCell({ flow, disabled }: Readonly<EditableCellProps>) {
  const { t } = useTranslation();
  const selection = useFlowSelectionOptional();
  if (!selection) return null;
  const checked = selection.isSelected(flow.id);
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      data-tab-cell
      data-testid={`flow-select-${flow.id}`}
      className={cn(
        "group/checkbox flex h-6 w-full items-center justify-center rounded-md outline-none transition-opacity duration-150",
        "disabled:cursor-not-allowed disabled:opacity-50",
        checked
          ? "opacity-100"
          : "opacity-0 group-hover/row:opacity-100 focus-visible:opacity-100",
      )}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        if (event.shiftKey) selection.selectRange(flow.id);
        else selection.toggle(flow.id);
        if (event.detail > 0) event.currentTarget.blur();
      }}
      aria-label={t("ordersRecap.flowBatch.selectLine", "Select line")}
    >
      <SelectionCheckboxBox checked={checked} disabled={disabled} />
    </button>
  );
}
